import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import toast, { Toaster } from 'react-hot-toast';
import { CheckCircleIcon, XCircleIcon, ClockIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import CircularProgress from '../components/CircularProgress';

// Automatische API-URL Erkennung für Production
const getApiUrl = () => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  
  // Production: Verwende die aktuelle Domain
  if (window.location.hostname !== 'localhost') {
    return `${window.location.origin}/api`;
  }
  
  // Development fallback - use proxy
  return '/api';
};

const API_URL = getApiUrl();

interface ElternbriefData {
  elternbrief: {
    id: number;
    titel: string;
    inhalt: string;
    module: Module[];
    anhaenge: Attachment[];
  };
  recipient: {
    id: number;
    schueler_name: string;
    klasse: string;
    empfaenger_name: string;
    geoeffnet_am: string | null;
    beantwortet_am: string | null;
    token_gueltig_bis: string;
  };
  readonly: boolean;
}

interface Module {
  id: number;
  modul_typ:
    | 'lesebestaetigung'
    | 'umfrage'
    | 'texteingabe'
    | 'lernbuerostatistik'
    | 'dateianhang'
    | 'terminbuchen'
    | 'zahlung'
    | 'elternportal_einladung';
  titel: string;
  konfiguration: any;
  pflichtfeld: boolean;
  fragen?: Frage[];
  existing_answer?: any;
  statistik?: any;
  statistik_generiert_am?: string;
  anhaenge?: Attachment[];
  appointment_config?: {
    id: number;
    title: string;
    description: string | null;
    public_token: string;
  };
  portal_status?: 'active' | 'pending' | 'expired' | 'none' | 'disabled';
  portal_invite_url?: string | null;
  portal_expires_at?: string | null;
}

interface Frage {
  id: number;
  frage_text: string;
  frage_typ: 'single_choice' | 'multiple_choice' | 'freitext' | 'bewertung';
  optionen: string[];
  pflichtfeld: boolean;
}

interface Attachment {
  id: number;
  dateiname: string;
  original_dateiname: string;
  mime_type: string;
  dateigroesse: number;
}

const ElternbriefPublic: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<ElternbriefData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Answers state
  const [answers, setAnswers] = useState<Record<number, any>>({});

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  useEffect(() => {
    loadElternbrief();
  }, [token]);

  const loadElternbrief = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_URL}/elternbrief-public/${token}`);
      
      if (response.data.success) {
        setData(response.data.data);
        
        // Initialize answers from existing answers
        const initialAnswers: Record<number, any> = {};
        response.data.data.elternbrief.module.forEach((modul: Module) => {
          if (modul.existing_answer) {
            initialAnswers[modul.id] = modul.existing_answer;
          }
        });
        setAnswers(initialAnswers);
      }
    } catch (error: any) {
      console.error('Error loading Elternbrief:', error);
      const errorData = error.response?.data;
      setError(errorData?.error || 'Fehler beim Laden');
      setErrorCode(errorData?.code || null);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!data) return;

    // Validate required fields
    for (const modul of data.elternbrief.module) {
      if (modul.pflichtfeld && !answers[modul.id]) {
        toast.error(`Bitte füllen Sie alle Pflichtfelder aus: ${modul.titel || 'Unbenanntes Modul'}`);
        return;
      }

      // Validate required survey questions
      if (modul.modul_typ === 'umfrage' && modul.fragen) {
        for (const frage of modul.fragen) {
          if (frage.pflichtfeld) {
            const moduleAnswer = answers[modul.id];
            if (!moduleAnswer || !moduleAnswer.umfrage_antworten) {
              toast.error(`Bitte beantworten Sie alle Pflichtfragen`);
              return;
            }
            
            const frageAnswer = moduleAnswer.umfrage_antworten.find((a: any) => a.frage_id === frage.id);
            if (!frageAnswer || (!frageAnswer.antwort_text && !frageAnswer.ausgewaehlte_optionen && !frageAnswer.bewertung)) {
              toast.error(`Bitte beantworten Sie die Frage: ${frage.frage_text}`);
              return;
            }
          }
        }
      }
    }

    try {
      setSubmitting(true);
      
      const antworten = Object.keys(answers).map(modulId => ({
        modul_id: parseInt(modulId),
        antwort_data: answers[parseInt(modulId)]
      }));

      const response = await axios.post(`${API_URL}/elternbrief-public/${token}/respond`, {
        antworten
      });

      if (response.data.success) {
        toast.success('Vielen Dank für Ihre Antwort!');
        // Reload to show readonly view
        setTimeout(() => {
          loadElternbrief();
        }, 1000);
      }
    } catch (error: any) {
      console.error('Error submitting response:', error);
      toast.error(error.response?.data?.error || 'Fehler beim Absenden');
    } finally {
      setSubmitting(false);
    }
  };

  const handleModuleAnswer = (modulId: number, answer: any) => {
    setAnswers(prev => ({
      ...prev,
      [modulId]: answer
    }));
  };

  const handleSurveyAnswer = (modulId: number, frageId: number, answer: any) => {
    setAnswers(prev => {
      const moduleAnswer = prev[modulId] || { umfrage_antworten: [] };
      const existingAnswerIndex = moduleAnswer.umfrage_antworten.findIndex((a: any) => a.frage_id === frageId);
      
      if (existingAnswerIndex >= 0) {
        moduleAnswer.umfrage_antworten[existingAnswerIndex] = { ...answer, frage_id: frageId };
      } else {
        moduleAnswer.umfrage_antworten.push({ ...answer, frage_id: frageId });
      }
      
      return {
        ...prev,
        [modulId]: moduleAnswer
      };
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          {errorCode === 'TOKEN_EXPIRED' ? (
            <>
              <ClockIcon className="h-16 w-16 mx-auto text-orange-500 mb-4" />
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Link abgelaufen</h2>
              <p className="text-gray-600">
                Dieser Link ist leider abgelaufen. Bitte wenden Sie sich an die Schule, um einen neuen Link zu erhalten.
              </p>
            </>
          ) : errorCode === 'ALREADY_ANSWERED' ? (
            <>
              <LockClosedIcon className="h-16 w-16 mx-auto text-blue-500 mb-4" />
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Bereits beantwortet</h2>
              <p className="text-gray-600">
                Ein anderer Sorgeberechtigter hat diesen Elternbrief bereits beantwortet.
              </p>
            </>
          ) : errorCode === 'TOKEN_NOT_FOUND' ? (
            <>
              <XCircleIcon className="h-16 w-16 mx-auto text-red-500 mb-4" />
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Link ungültig</h2>
              <p className="text-gray-600">
                Dieser Link ist ungültig oder existiert nicht mehr.
              </p>
            </>
          ) : (
            <>
              <XCircleIcon className="h-16 w-16 mx-auto text-red-500 mb-4" />
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Fehler</h2>
              <p className="text-gray-600">{error}</p>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <Toaster position="top-center" />
      
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="text-center mb-4">
            <div className="inline-block p-3 bg-blue-100 rounded-full mb-3">
              <svg className="h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 19v-8.93a2 2 0 01.89-1.664l7-4.666a2 2 0 012.22 0l7 4.666A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M3 10l6.75 4.5M21 10l-6.75 4.5m0 0l-1.14.76a2 2 0 01-2.22 0l-1.14-.76" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              {data.elternbrief.titel}
            </h1>
            <p className="text-gray-600">
              Für: {data.recipient.schueler_name} ({data.recipient.klasse})
            </p>
          </div>

          {data.readonly && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
              <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-green-800 mb-1">Bereits beantwortet</h3>
                <p className="text-sm text-green-700">
                  Sie haben diesen Elternbrief am {new Date(data.recipient.beantwortet_am!).toLocaleString('de-DE')} beantwortet.
                </p>
              </div>
            </div>
          )}

          {!data.readonly && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-700">
                <strong>Gültig bis:</strong> {new Date(data.recipient.token_gueltig_bis).toLocaleDateString('de-DE')}
              </p>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="prose max-w-none">
            <div className="whitespace-pre-wrap text-gray-700">
              {data.elternbrief.inhalt}
            </div>
          </div>
        </div>

        {/* Modules */}
        {data.elternbrief.module.map((modul) => (
          <div key={modul.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            {modul.titel && (
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                {modul.titel}
                {modul.pflichtfeld && <span className="text-red-500 ml-1">*</span>}
              </h3>
            )}

            {/* Lesebestätigung */}
            {modul.modul_typ === 'lesebestaetigung' && (
              <div>
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={answers[modul.id]?.checkbox_bestaetigt || false}
                    onChange={(e) => handleModuleAnswer(modul.id, { checkbox_bestaetigt: e.target.checked })}
                    disabled={data.readonly}
                    className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                  />
                  <span className="text-gray-700">
                    Ich habe den Elternbrief gelesen und zur Kenntnis genommen.
                    {modul.pflichtfeld && <span className="text-red-500 ml-1">*</span>}
                  </span>
                </label>
              </div>
            )}

            {/* Umfrage */}
            {modul.modul_typ === 'umfrage' && modul.fragen && (
              <div className="space-y-6">
                {modul.fragen.map((frage) => (
                  <div key={frage.id} className="border-b border-gray-200 pb-6 last:border-0 last:pb-0">
                    <label className="block font-medium text-gray-700 mb-3">
                      {frage.frage_text}
                      {frage.pflichtfeld && <span className="text-red-500 ml-1">*</span>}
                    </label>

                    {/* Single Choice */}
                    {frage.frage_typ === 'single_choice' && (
                      <div className="space-y-2">
                        {frage.optionen.map((option, index) => (
                          <label key={index} className="flex items-center gap-2">
                            <input
                              type="radio"
                              name={`frage-${frage.id}`}
                              value={option}
                              checked={
                                answers[modul.id]?.umfrage_antworten?.find((a: any) => a.frage_id === frage.id)
                                  ?.ausgewaehlte_optionen?.[0] === option
                              }
                              onChange={(e) => {
                                if (e.target.checked) {
                                  handleSurveyAnswer(modul.id, frage.id, {
                                    ausgewaehlte_optionen: [option]
                                  });
                                }
                              }}
                              disabled={data.readonly}
                              className="text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                            />
                            <span className="text-gray-700">{option}</span>
                          </label>
                        ))}
                      </div>
                    )}

                    {/* Multiple Choice */}
                    {frage.frage_typ === 'multiple_choice' && (
                      <div className="space-y-2">
                        {frage.optionen.map((option, index) => {
                          const selectedOptions =
                            answers[modul.id]?.umfrage_antworten?.find((a: any) => a.frage_id === frage.id)
                              ?.ausgewaehlte_optionen || [];
                          
                          return (
                            <label key={index} className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                value={option}
                                checked={selectedOptions.includes(option)}
                                onChange={(e) => {
                                  let newOptions;
                                  if (e.target.checked) {
                                    newOptions = [...selectedOptions, option];
                                  } else {
                                    newOptions = selectedOptions.filter((o: string) => o !== option);
                                  }
                                  handleSurveyAnswer(modul.id, frage.id, {
                                    ausgewaehlte_optionen: newOptions
                                  });
                                }}
                                disabled={data.readonly}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                              />
                              <span className="text-gray-700">{option}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}

                    {/* Freitext */}
                    {frage.frage_typ === 'freitext' && (
                      <textarea
                        value={
                          answers[modul.id]?.umfrage_antworten?.find((a: any) => a.frage_id === frage.id)
                            ?.antwort_text || ''
                        }
                        onChange={(e) => handleSurveyAnswer(modul.id, frage.id, { antwort_text: e.target.value })}
                        disabled={data.readonly}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:bg-gray-50"
                        placeholder="Ihre Antwort..."
                      />
                    )}

                    {/* Bewertung */}
                    {frage.frage_typ === 'bewertung' && (
                      <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map((rating) => (
                          <button
                            key={rating}
                            type="button"
                            onClick={() => handleSurveyAnswer(modul.id, frage.id, { bewertung: rating })}
                            disabled={data.readonly}
                            className={`w-12 h-12 rounded-lg border-2 font-semibold transition-colors disabled:opacity-50 ${
                              answers[modul.id]?.umfrage_antworten?.find((a: any) => a.frage_id === frage.id)
                                ?.bewertung === rating
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'bg-white text-gray-700 border-gray-300 hover:border-blue-600'
                            }`}
                          >
                            {rating}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Texteingabe */}
            {modul.modul_typ === 'texteingabe' && (
              <div>
                <textarea
                  value={answers[modul.id]?.text || ''}
                  onChange={(e) => handleModuleAnswer(modul.id, { text: e.target.value })}
                  disabled={data.readonly}
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:bg-gray-50"
                  placeholder="Ihre Rückmeldung..."
                />
              </div>
            )}

            {/* Dateianhang */}
            {modul.modul_typ === 'dateianhang' && (
              <div className="bg-teal-50 p-4 rounded-lg">
                {modul.anhaenge && modul.anhaenge.length > 0 ? (
                  <div className="space-y-2">
                    {modul.anhaenge.map((attachment) => (
                      <a
                        key={attachment.id}
                        href={`${API_URL}/elternbrief-public/${token}/attachment/${attachment.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 bg-white border border-teal-200 rounded-lg hover:bg-teal-50 transition-colors"
                      >
                        <svg className="h-6 w-6 text-teal-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        <div className="flex-1 min-w-0">
                          <p className="text-blue-600 hover:text-blue-700 font-medium truncate">
                            {attachment.original_dateiname}
                          </p>
                          <p className="text-xs text-gray-600">
                            {formatFileSize(attachment.dateigroesse)}
                          </p>
                        </div>
                        <svg className="h-5 w-5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-600 text-center py-4">Keine Dateien verfügbar</p>
                )}
              </div>
            )}

            {/* Lernbürostatistik */}
            {modul.modul_typ === 'lernbuerostatistik' && (
              <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-lg p-6 border border-orange-200">
                {modul.statistik ? (
                  <div>
                    {/* Header */}
                    <div className="mb-6 text-center">
                      <h3 className="text-xl font-bold text-gray-900 mb-1">
                        Lernbürostatistik für {modul.statistik.student?.name}
                      </h3>
                      <div className="text-sm text-gray-600">
                        {modul.statistik.dateRange?.label || 'Aktuell'} • Klasse {modul.statistik.student?.klasse}
                      </div>
                    </div>

                    {/* Circular Progress Views */}
                    <div className="grid grid-cols-2 gap-6 mb-4">
                      {/* Deutsch */}
                      {modul.statistik.progress?.Deutsch && (
                        <CircularProgress
                          progress={modul.statistik.progress.Deutsch}
                          color="text-yellow-500"
                          size={100}
                        />
                      )}
                      
                      {/* Mathematik */}
                      {modul.statistik.progress?.Mathematik && (
                        <CircularProgress
                          progress={modul.statistik.progress.Mathematik}
                          color="text-blue-500"
                          size={100}
                        />
                      )}
                      
                      {/* Englisch */}
                      {modul.statistik.progress?.Englisch && (
                        <CircularProgress
                          progress={modul.statistik.progress.Englisch}
                          color="text-red-500"
                          size={100}
                        />
                      )}
                      
                      {/* Gesamt */}
                      {modul.statistik.progress?.Gesamt && (
                        <CircularProgress
                          progress={modul.statistik.progress.Gesamt}
                          color="text-green-500"
                          size={100}
                        />
                      )}
                    </div>

                    {/* Footer Info */}
                    <div className="pt-4 border-t border-orange-200">
                      <p className="text-xs text-gray-600 text-center">
                        Diese Statistik zeigt den aktuellen Lernfortschritt im Lernbüro.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-sm text-gray-600">Noch keine Statistik generiert.</div>
                  </div>
                )}
              </div>
            )}

            {/* Terminbuchen */}
            {modul.modul_typ === 'terminbuchen' && (
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg p-6 border border-indigo-200">
                {modul.appointment_config ? (
                  <div className="text-center">
                    <div className="mb-4">
                      <svg className="h-12 w-12 text-indigo-600 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {modul.appointment_config.title}
                      </h3>
                      {modul.appointment_config.description && (
                        <p className="text-sm text-gray-600 mb-4">
                          {modul.appointment_config.description}
                        </p>
                      )}
                    </div>
                    <a
                      href={`/appointments/${modul.appointment_config.public_token}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-semibold"
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Termin buchen
                    </a>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-600">
                      Terminbuchungs-Konfiguration nicht gefunden.
                    </p>
                  </div>
                )}
              </div>
            )}

            {modul.modul_typ === 'elternportal_einladung' && (
              <div className="bg-cyan-50 rounded-lg p-6 border border-cyan-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Lernrakete-Elternportal</h3>
                {modul.konfiguration?.info_text ? (
                  <p className="text-sm text-gray-700 mb-3 whitespace-pre-wrap">{modul.konfiguration.info_text}</p>
                ) : null}

                {modul.portal_status === 'disabled' && (
                  <p className="text-sm text-gray-600">
                    Das Elternportal ist an Ihrer Schule derzeit nicht verfügbar. Der Elternbrief selbst
                    können Sie hier trotzdem öffnen und beantworten.
                  </p>
                )}

                {modul.portal_status === 'active' && (
                  <div className="space-y-2">
                    <p className="text-sm text-emerald-700">
                      Sie haben bereits Zugang zum Elternportal.
                    </p>
                    <a
                      href="/login"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-semibold"
                    >
                      Zum Login
                    </a>
                  </div>
                )}

                {modul.portal_status === 'pending' && (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-700">
                      Sie können Ihr Elternportal jetzt einrichten.
                    </p>
                    {modul.portal_invite_url ? (
                      <a
                        href={modul.portal_invite_url}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors font-semibold"
                      >
                        Elternportal einrichten
                      </a>
                    ) : (
                      <p className="text-sm text-amber-700">Einladungslink wird vorbereitet. Bitte laden Sie die Seite neu.</p>
                    )}
                    {modul.portal_expires_at ? (
                      <p className="text-xs text-gray-500">
                        Gültig bis {new Date(modul.portal_expires_at).toLocaleDateString('de-DE')}
                      </p>
                    ) : null}
                  </div>
                )}

                {modul.portal_status === 'expired' && (
                  <p className="text-sm text-amber-700">
                    Die Einladung zum Elternportal ist abgelaufen. Bitte wenden Sie sich an die Schule.
                  </p>
                )}

                {(!modul.portal_status || modul.portal_status === 'none') && (
                  <p className="text-sm text-gray-600">
                    Für diese E-Mail liegt aktuell keine aktive Elternportal-Einladung vor.
                  </p>
                )}
              </div>
            )}
          </div>
        ))}

        {/* Submit Button */}
        {!data.readonly && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-lg"
            >
              {submitting ? 'Wird gesendet...' : 'Absenden'}
            </button>
            <p className="text-center text-sm text-gray-500 mt-3">
              Felder mit <span className="text-red-500">*</span> sind Pflichtfelder
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ElternbriefPublic;

